import React from 'react';
import { Close, Error } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';

export type CustomDialogProps = {
  title?: React.ReactNode;
  content?: React.ReactNode;
  actions?: React.ReactNode;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const SlideDialog: React.FC<CustomDialogProps & DialogProps> = ({
  title,
  content,
  actions,
  children,
  ...dialogProps
}) => {
  return (
    <Dialog
      {...dialogProps}
      TransitionComponent={Transition}
      keepMounted
      hideBackdrop
    >
      {title && <DialogTitle>{title}</DialogTitle>}
      {content && <DialogContent>{content}</DialogContent>}
      {actions && <DialogActions>{actions}</DialogActions>}
      {children}
    </Dialog>
  );
};

export const FullHeightSlideDialog = styled(SlideDialog)(() => ({
  '& .MuiDialog-container': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  '& .MuiPaper-root': {
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    maxWidth: '1024px',
    height: '100%',
    width: '100%',
    padding: 0,
    margin: 0,
    boxShadow: '0px 0px 24px 4px #121C2D33',
    bottom: 0,
    position: 'relative',
  },
}));

export const WarningDialog: React.FC<CustomDialogProps & DialogProps> = ({
  title,
  content,
  onClose,
  ...props
}) => {
  return (
    <Dialog {...props}>
      <IconButton
        aria-label="close"
        onClick={(e) => onClose && onClose(e, 'escapeKeyDown')}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'text.dark',
        }}
      >
        <Close fontSize="small" />
      </IconButton>
      <DialogContent>
        <Stack gap={3} pt={2}>
          <Stack gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <Error fontSize="small" sx={{ mr: 1 }} color="error" />
              <Typography fontSize="16px" color="text.dark" fontWeight="600">
                {title}
              </Typography>
            </Box>

            <Typography
              fontSize="14px"
              color="text.placeholder"
              fontWeight="400"
            >
              {content}
            </Typography>
          </Stack>
          <Button
            fullWidth
            variant="outlined"
            onClick={(e) => onClose && onClose(e, 'escapeKeyDown')}
            sx={{
              borderRadius: '100px',
              borderColor: 'text.charcoal',
            }}
          >
            <Typography variant="body2" fontWeight={500} color="text.charcoal">
              OK
            </Typography>
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
